import { Button } from '@citizen-walk/shared-ui';
import {
  DECORATION_SLOT_LABELS,
  DEFAULT_CARD_THEME,
  FONT_FAMILY_OPTIONS,
  cloneTheme,
  type CardThemeLayout,
  type Decoration,
  type DecorationSlot,
  type DecorationTint,
  type ResolvedCardTheme,
} from './cardTheme';
import { CARD_PRESETS } from './cardPresets';
import { ColorInput } from './ColorInput';
import { readSvgFile } from './svgUtils';

/**
 * AW6 — Card-print 視覚テーマエディタ。old2 `ThemeEditor.tsx` + `themes.ts` を v2 に移植。
 *
 * 機能:
 *   - テーマプリセット (Sakura/Forest/Pop/Minimal/既定) のワンクリック適用グリッド
 *   - カラー (背景 / メイン(accent) / 副色(secondary) / 見出し色 / 本文色)
 *   - フォント family
 *   - レイアウト (写真位置/幅 / 文字余白 / 見出し・本文フォントサイズ)
 *   - 枠線 (色 / 太さ)
 *   - ロゴ画像 URL
 *   - 装飾 SVG (slot / 位置 / 寸法 / tint(accent/secondary/none) / opacity / rotation / SVG 差替)
 *
 * onChange は state を上位に通知するだけ (M7 master 永続化は上位が themeToPayload 経由で実行)。
 *
 * カテゴリバッジ色 (theme.categoryColors) はこのタブでは編集しない。「項目・印刷」タブの
 * カテゴリバッジ書式設定 (FieldsEditor / CategoryBadgeStyleEditor) に一本化しており、
 * プリセット (テーマ) を切替えても現在の categoryColors は維持される (applyPreset で引き継ぐ)。
 */

const COLOR_FIELDS: { key: keyof ResolvedCardTheme; label: string }[] = [
  { key: 'backgroundColor', label: '背景' },
  { key: 'accent', label: 'メイン' },
  { key: 'secondary', label: '副色' },
  { key: 'textHeading', label: '見出し' },
  { key: 'textColor', label: '本文' },
];

export interface ThemeEditorProps {
  theme: ResolvedCardTheme;
  /** 編集中の値を上位に通知。M7 master 永続化は上位が `themeToPayload` 経由で実行。 */
  onChange: (next: ResolvedCardTheme) => void;
  /** master role でなければ disabled (色変更 / SVG 追加 を block)。 */
  disabled?: boolean;
}

export function ThemeEditor({ theme, onChange, disabled }: ThemeEditorProps): JSX.Element {
  function setColor(key: keyof ResolvedCardTheme, value: string): void {
    onChange({ ...theme, [key]: value });
  }

  function setFont(value: string): void {
    onChange({ ...theme, fontFamily: value });
  }

  function setLogoUrl(value: string): void {
    onChange({ ...theme, logoUrl: value });
  }

  function setLayout(patch: Partial<CardThemeLayout>): void {
    onChange({ ...theme, layout: { ...theme.layout, ...patch } });
  }

  function setBorder(patch: Partial<ResolvedCardTheme['border']>): void {
    onChange({ ...theme, border: { ...theme.border, ...patch } });
  }

  function updateDecoration(index: number, patch: Partial<Decoration>): void {
    const next = theme.decorations.map((d, i) => (i === index ? { ...d, ...patch } : d));
    onChange({ ...theme, decorations: next });
  }
  function removeDecoration(index: number): void {
    onChange({ ...theme, decorations: theme.decorations.filter((_, i) => i !== index) });
  }
  async function addDecoration(file: File): Promise<void> {
    try {
      const cleaned = await readSvgFile(file);
      const newDecoration: Decoration = {
        slot: 'corner-top-left',
        svgSource: cleaned,
        x_mm: 2,
        y_mm: 2,
        width_mm: 10,
        height_mm: 10,
        tintWith: 'accent',
      };
      onChange({ ...theme, decorations: [...theme.decorations, newDecoration] });
    } catch (e) {
      alert(`SVG の読込に失敗しました: ${(e as Error).message}`);
    }
  }
  async function replaceDecorationSvg(index: number, file: File): Promise<void> {
    try {
      const cleaned = await readSvgFile(file);
      updateDecoration(index, { svgSource: cleaned });
    } catch (e) {
      alert(`SVG の読込に失敗しました: ${(e as Error).message}`);
    }
  }

  function applyPreset(presetId: string): void {
    const preset = CARD_PRESETS.find((p) => p.id === presetId);
    // プリセット適用時も現在のカテゴリバッジ色 (項目・印刷タブで指定) は引き継ぎ、上書きしない。
    if (preset)
      onChange({ ...cloneTheme(preset.theme), categoryColors: { ...theme.categoryColors } });
  }
  function resetTheme(): void {
    // リセットも現在のカテゴリバッジ色は維持 (色は項目・印刷タブの管轄)。
    onChange({ ...cloneTheme(DEFAULT_CARD_THEME), categoryColors: { ...theme.categoryColors } });
  }

  return (
    <div className="grid grid-cols-1 gap-3 text-sm" data-testid="card-theme-editor">
      <section>
        <h3 className="font-bold text-slate-800 mb-2">プリセット</h3>
        <div className="grid grid-cols-3 gap-1" data-testid="card-theme-presets">
          {CARD_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => applyPreset(p.id)}
              disabled={disabled}
              className="px-2 py-2 text-xs border border-slate-200 rounded text-left hover:border-primary-400 disabled:opacity-40"
              aria-label={`プリセット ${p.label} を適用`}
              style={{ background: p.theme.backgroundColor }}
            >
              <span className="block font-semibold" style={{ color: p.theme.textHeading }}>
                {p.label}
              </span>
              <span className="flex gap-0.5 mt-1">
                <span
                  className="inline-block w-3 h-3 rounded-sm"
                  style={{ background: p.theme.accent }}
                />
                <span
                  className="inline-block w-3 h-3 rounded-sm"
                  style={{ background: p.theme.secondary }}
                />
              </span>
            </button>
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-slate-800">カラー</h3>
          <button
            type="button"
            onClick={resetTheme}
            disabled={disabled}
            className="text-[10px] text-slate-500 hover:text-slate-700 underline disabled:opacity-40"
            aria-label="テーマを既定値にリセット"
          >
            リセット
          </button>
        </div>
        <div className="grid grid-cols-1 gap-2">
          {COLOR_FIELDS.map(({ key, label }) => (
            <div key={key} className="grid grid-cols-1 gap-0.5 text-[10px] text-slate-600">
              {label}
              <ColorInput
                value={(theme[key] as string) || '#000000'}
                onChange={(v) => setColor(key, v)}
                disabled={disabled}
                ariaLabel={`${label}色`}
              />
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3 className="font-bold text-slate-800 mb-2">フォント</h3>
        <select
          value={theme.fontFamily}
          onChange={(e) => setFont(e.target.value)}
          disabled={disabled}
          aria-label="フォントファミリー"
          className="w-full text-xs border border-slate-300 rounded px-2 py-1 bg-white"
        >
          {FONT_FAMILY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </section>

      <section>
        <h3 className="font-bold text-slate-800 mb-2">レイアウト</h3>
        <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-600">
          <label className="grid grid-cols-1 gap-0.5">
            写真の位置
            <select
              value={theme.layout.photoSide}
              onChange={(e) => setLayout({ photoSide: e.target.value as 'left' | 'right' })}
              disabled={disabled}
              aria-label="写真の位置"
              className="px-1 py-0.5 border border-slate-300 rounded bg-white"
            >
              <option value="left">左</option>
              <option value="right">右</option>
            </select>
          </label>
          <label className="grid grid-cols-1 gap-0.5">
            写真幅(mm)
            <input
              type="number"
              step="0.5"
              value={theme.layout.photoWidth_mm}
              onChange={(e) => setLayout({ photoWidth_mm: parseFloat(e.target.value) || 0 })}
              disabled={disabled}
              aria-label="写真幅"
              className="px-1 py-0.5 border border-slate-300 rounded"
            />
          </label>
          <label className="grid grid-cols-1 gap-0.5">
            文字余白(mm)
            <input
              type="number"
              step="0.5"
              value={theme.layout.textPadding_mm}
              onChange={(e) => setLayout({ textPadding_mm: parseFloat(e.target.value) || 0 })}
              disabled={disabled}
              aria-label="文字余白"
              className="px-1 py-0.5 border border-slate-300 rounded"
            />
          </label>
          <label className="grid grid-cols-1 gap-0.5">
            見出し(pt)
            <input
              type="number"
              step="0.5"
              value={theme.layout.titleFontSize_pt}
              onChange={(e) => setLayout({ titleFontSize_pt: parseFloat(e.target.value) || 0 })}
              disabled={disabled}
              aria-label="見出しフォントサイズ"
              className="px-1 py-0.5 border border-slate-300 rounded"
            />
          </label>
          <label className="grid grid-cols-1 gap-0.5">
            本文(pt)
            <input
              type="number"
              step="0.5"
              value={theme.layout.bodyFontSize_pt}
              onChange={(e) => setLayout({ bodyFontSize_pt: parseFloat(e.target.value) || 0 })}
              disabled={disabled}
              aria-label="本文フォントサイズ"
              className="px-1 py-0.5 border border-slate-300 rounded"
            />
          </label>
        </div>
      </section>

      <section>
        <h3 className="font-bold text-slate-800 mb-2">枠線</h3>
        <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-600">
          <div className="grid grid-cols-1 gap-0.5">
            色
            <ColorInput
              value={theme.border.color}
              onChange={(v) => setBorder({ color: v })}
              disabled={disabled}
              ariaLabel="枠線色"
            />
          </div>
          <label className="grid grid-cols-1 gap-0.5">
            太さ(mm、0で非表示)
            <input
              type="number"
              step="0.1"
              min="0"
              value={theme.border.width_mm}
              onChange={(e) => setBorder({ width_mm: parseFloat(e.target.value) || 0 })}
              disabled={disabled}
              aria-label="枠線の太さ"
              className="px-1 py-0.5 border border-slate-300 rounded"
            />
          </label>
        </div>
      </section>

      <section>
        <h3 className="font-bold text-slate-800 mb-2">ロゴ画像 URL</h3>
        <input
          type="url"
          value={theme.logoUrl}
          onChange={(e) => setLogoUrl(e.target.value)}
          disabled={disabled}
          placeholder="https://example.com/logo.png"
          aria-label="ロゴ画像 URL"
          className="w-full text-xs border border-slate-300 rounded px-2 py-1"
        />
        <p className="text-[10px] text-slate-400 mt-1">
          http/https の絶対 URL のみ。空欄で非表示。右下に小さく表示されます。
        </p>
      </section>

      <section>
        <h3 className="font-bold text-slate-800 mb-2">装飾 SVG ({theme.decorations.length})</h3>
        <div className="grid grid-cols-1 gap-2">
          {theme.decorations.map((d, i) => (
            <div
              key={i}
              className="border border-slate-200 rounded p-2 grid grid-cols-1 gap-1 text-xs bg-slate-50"
              data-testid="decoration-row"
            >
              <div className="flex items-center gap-1">
                <select
                  value={d.slot}
                  onChange={(e) => updateDecoration(i, { slot: e.target.value as DecorationSlot })}
                  disabled={disabled}
                  aria-label={`装飾 ${i + 1} スロット`}
                  className="flex-1 px-1 py-0.5 border border-slate-300 rounded text-[11px]"
                >
                  {Object.entries(DECORATION_SLOT_LABELS).map(([k, label]) => (
                    <option key={k} value={k}>
                      {label}
                    </option>
                  ))}
                </select>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => removeDecoration(i)}
                  disabled={disabled}
                  aria-label={`装飾 ${i + 1} を削除`}
                >
                  ✕
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-1 text-[10px]">
                <label>
                  X(mm)
                  <input
                    type="number"
                    step="0.1"
                    value={d.x_mm}
                    onChange={(e) => updateDecoration(i, { x_mm: parseFloat(e.target.value) || 0 })}
                    disabled={disabled}
                    aria-label={`装飾 ${i + 1} X座標`}
                    className="w-full px-1 py-0.5 border border-slate-300 rounded"
                  />
                </label>
                <label>
                  Y(mm)
                  <input
                    type="number"
                    step="0.1"
                    value={d.y_mm}
                    onChange={(e) => updateDecoration(i, { y_mm: parseFloat(e.target.value) || 0 })}
                    disabled={disabled}
                    aria-label={`装飾 ${i + 1} Y座標`}
                    className="w-full px-1 py-0.5 border border-slate-300 rounded"
                  />
                </label>
                <label>
                  幅(mm)
                  <input
                    type="number"
                    step="0.1"
                    value={d.width_mm}
                    onChange={(e) =>
                      updateDecoration(i, { width_mm: parseFloat(e.target.value) || 0 })
                    }
                    disabled={disabled}
                    aria-label={`装飾 ${i + 1} 幅`}
                    className="w-full px-1 py-0.5 border border-slate-300 rounded"
                  />
                </label>
                <label>
                  高さ(mm)
                  <input
                    type="number"
                    step="0.1"
                    value={d.height_mm}
                    onChange={(e) =>
                      updateDecoration(i, { height_mm: parseFloat(e.target.value) || 0 })
                    }
                    disabled={disabled}
                    aria-label={`装飾 ${i + 1} 高さ`}
                    className="w-full px-1 py-0.5 border border-slate-300 rounded"
                  />
                </label>
                <label>
                  不透明度
                  <input
                    type="number"
                    step="0.05"
                    min="0"
                    max="1"
                    value={d.opacity ?? 1}
                    onChange={(e) =>
                      updateDecoration(i, {
                        opacity: Math.max(0, Math.min(1, parseFloat(e.target.value) || 0)),
                      })
                    }
                    disabled={disabled}
                    aria-label={`装飾 ${i + 1} 不透明度`}
                    className="w-full px-1 py-0.5 border border-slate-300 rounded"
                  />
                </label>
                <label>
                  回転(°)
                  <input
                    type="number"
                    step="1"
                    value={d.rotation_deg ?? 0}
                    onChange={(e) =>
                      updateDecoration(i, { rotation_deg: parseFloat(e.target.value) || 0 })
                    }
                    disabled={disabled}
                    aria-label={`装飾 ${i + 1} 回転`}
                    className="w-full px-1 py-0.5 border border-slate-300 rounded"
                  />
                </label>
              </div>
              <select
                value={d.tintWith ?? 'none'}
                onChange={(e) =>
                  updateDecoration(i, { tintWith: e.target.value as DecorationTint })
                }
                disabled={disabled}
                aria-label={`装飾 ${i + 1} 着色`}
                className="px-1 py-0.5 border border-slate-300 rounded text-[11px] bg-white"
              >
                <option value="none">着色なし</option>
                <option value="accent">メイン色で着色</option>
                <option value="secondary">副色で着色</option>
              </select>
              <label className="text-[10px] text-slate-600">
                SVG ファイル差替:
                <input
                  type="file"
                  accept=".svg,image/svg+xml"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void replaceDecorationSvg(i, f);
                    e.target.value = '';
                  }}
                  disabled={disabled}
                  aria-label={`装飾 ${i + 1} の SVG ファイルを差替`}
                  className="block w-full text-[10px]"
                />
              </label>
            </div>
          ))}
        </div>
        <label className="block mt-2 text-[10px] text-slate-600">
          + SVG を追加:
          <input
            type="file"
            accept=".svg,image/svg+xml"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void addDecoration(f);
              e.target.value = '';
            }}
            disabled={disabled}
            aria-label="装飾 SVG を追加"
            className="block w-full text-[10px]"
          />
        </label>
      </section>
    </div>
  );
}
